import { useCallback, useEffect, useMemo, useState } from "react";
import { useDateRange } from "@/state/DateRangeContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import {
  fetchAgentChatOps,
  fetchChatOverview,
  fetchChatVolume,
  fetchOpenByAssignee,
  fetchTransferStats,
} from "@/api/reportingChatApi";
import { getSlaSettings } from "@/api/slaSettingsApi";
import { getAgentRoutingSettings } from "@/api/agentRoutingApi";
import { humanAgentQaReportingService } from "@/pages/analytics/services/HumanAgentQaReportingService";
import { humanAgentQaSummaryDeriver } from "@/pages/analytics/lib/HumanAgentQaSummaryDeriver";
import {
  buildChatKpis,
  buildChatStatusShareRows,
  chatVolumeDaysForVolumeBars,
  chatVolumeMaxFromSeries,
  countChatSlaAtRisk,
  countChatSlaAtRiskForAgent,
} from "@/pages/analytics/lib/reportingChatDerive";

/** @param {unknown} payload @param {string} label */
function assertReportingPayload(payload, label) {
  if (!payload || typeof payload !== "object") {
    throw new Error(`${label}: invalid response`);
  }
  const o = /** @type {Record<string, unknown>} */ (payload);
  if (o.error) throw new Error(String(o.error));
}

/**
 * Workspace-scoped Live chat reporting (five BE calls + local SLA merge).
 * @param {{ enabled?: boolean }} [opts]
 */
export function useReportingChats({ enabled = true } = {}) {
  const { startDate, endDate } = useDateRange();
  const { activeWorkspaceId } = useWorkspace();
  const effective = Boolean(enabled && activeWorkspaceId);
  const range = useMemo(() => ({ startDate, endDate }), [startDate, endDate]);

  const [chatOverview, setChatOverview] = useState(/** @type {Record<string, unknown> | null} */ (null));
  const [transferStats, setTransferStats] = useState(/** @type {Record<string, unknown> | null} */ (null));
  const [chatVolume, setChatVolume] = useState(/** @type {Array<Record<string, unknown>> | null} */ (null));
  const [openByAssignee, setOpenByAssignee] = useState(/** @type {Array<{ name: string, count: number }> | null} */ (null));
  const [agentChatOps, setAgentChatOps] = useState(/** @type {Array<Record<string, unknown>> | null> */ (null));
  const [slaSettings, setSlaSettings] = useState(
    /** @type {{ chatFirstResponseSeconds: number, chatAverageResponseSeconds: number } | null} */ (null)
  );
  const [routingAgents, setRoutingAgents] = useState(/** @type {Array<{ name: string, maxConcurrentChats: number }> | null} */ (null));
  const [leaderboard, setLeaderboard] = useState(/** @type {Array<Record<string, unknown>> | null} */ (null));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(/** @type {Error | null} */ (null));
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!effective) {
      setLoading(false);
      setError(null);
      setChatOverview(null);
      setTransferStats(null);
      setChatVolume(null);
      setOpenByAssignee(null);
      setAgentChatOps(null);
      setSlaSettings(null);
      setRoutingAgents(null);
      setLeaderboard(null);
      return undefined;
    }

    const ac = new AbortController();
    setLoading(true);
    setError(null);

    const init = { signal: ac.signal };

    Promise.all([
      fetchChatOverview(range, init),
      fetchTransferStats(range, init),
      fetchChatVolume(range, init),
      fetchOpenByAssignee(range, init),
      fetchAgentChatOps(range, init),
      getSlaSettings(),
      getAgentRoutingSettings(),
      humanAgentQaReportingService.load(range, ac.signal).catch((e) => {
        humanAgentQaReportingService.logError(e instanceof Error ? e : new Error(String(e)));
        return [];
      }),
    ])
      .then(([ov, tr, vol, oba, ops, slaRes, routeRes, leaderboard]) => {
        if (ac.signal.aborted) return;
        assertReportingPayload(ov, "chat-overview");
        assertReportingPayload(tr, "transfers");
        assertReportingPayload(vol, "chat-volume");
        assertReportingPayload(oba, "open-by-assignee");
        assertReportingPayload(ops, "agent-chat-ops");
        setChatOverview(/** @type {Record<string, unknown>} */ (ov));
        setTransferStats(/** @type {Record<string, unknown>} */ (tr));
        setChatVolume(Array.isArray(vol.data) ? /** @type {Array<Record<string, unknown>>} */ (vol.data) : []);
        setOpenByAssignee(Array.isArray(oba.data) ? /** @type {Array<{ name: string, count: number }>} */ (oba.data) : []);
        setAgentChatOps(Array.isArray(ops.data) ? /** @type {Array<Record<string, unknown>>} */ (ops.data) : []);
        setSlaSettings(slaRes?.data ?? null);
        setRoutingAgents(
          Array.isArray(routeRes?.data?.agents)
            ? routeRes.data.agents.map((a) => ({
                name: String(a.name),
                maxConcurrentChats: Number(a.maxConcurrentChats) || 0,
              }))
            : []
        );
        setLeaderboard(Array.isArray(leaderboard) ? leaderboard : []);
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setChatOverview(null);
        setTransferStats(null);
        setChatVolume(null);
        setOpenByAssignee(null);
        setAgentChatOps(null);
        setSlaSettings(null);
        setRoutingAgents(null);
        setLeaderboard(null);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });

    return () => ac.abort();
  }, [range, effective, tick]);

  const chats = useMemo(() => {
    const openSessions = Array.isArray(chatOverview?.open_sessions)
      ? /** @type {Array<{ handoff_time: number, first_response_time: number | null, agent_id: string | null }>} */ (
          chatOverview.open_sessions
        )
      : [];
    const slaRisk =
      chatOverview?.chat_sla_at_risk_open != null
        ? Number(chatOverview.chat_sla_at_risk_open) || 0
        : slaSettings && openSessions.length
          ? countChatSlaAtRisk(openSessions, slaSettings)
          : 0;
    const transferTotal = Number(transferStats?.total) || 0;
    const kpis = chatOverview ? buildChatKpis(chatOverview, transferTotal, slaRisk) : [];
    const chatStatusShare = chatOverview ? buildChatStatusShareRows(chatOverview) : [];
    const volumeRaw = chatVolume ?? [];
    const chatVolumeWeek = chatVolumeDaysForVolumeBars(volumeRaw);
    const chatVolumeMax = chatVolumeMaxFromSeries(volumeRaw);
    const assigneeBars = openByAssignee ?? [];
    const maxAssignee = assigneeBars.length
      ? Math.max(1, ...assigneeBars.map((b) => Number(b.count) || 0))
      : 1;

    const capByName = new Map((routingAgents ?? []).map((a) => [a.name, a.maxConcurrentChats]));
    const leaderboardById = new Map(
      (leaderboard ?? []).map((agent) => [String(agent.id), agent]),
    );
    const agentRows = (agentChatOps ?? []).map((row) => {
      const name = String(row.name ?? "");
      const agentAccountId = String(row.agentAccountId ?? "");
      const atRisk =
        row.atRiskChats != null
          ? Number(row.atRiskChats) || 0
          : slaSettings && agentAccountId
            ? countChatSlaAtRiskForAgent(agentAccountId, openSessions, slaSettings)
            : 0;
      const lb = leaderboardById.get(agentAccountId);
      return {
        name,
        agentAccountId,
        openChats: Number(row.openChats) || 0,
        transfersIn: Number(row.transfersIn) || 0,
        atRiskChats: atRisk,
        slaBreachCount: Number(row.slaBreachCount) || 0,
        firstResponse: Number(lb?.metrics?.first_response_time) || 0,
        avgResponse: Number(lb?.metrics?.avg_response_time) || 0,
        maxConcurrent: capByName.has(name) ? capByName.get(name) : "—",
      };
    });

    const meta = chatOverview
      ? {
          conversationTotal: Number(chatOverview.conversation_total) || 0,
          transferEvents: transferTotal,
          resolvedChats: Number(chatOverview.resolved_count) || 0,
        }
      : {};

    const haQa = humanAgentQaSummaryDeriver.build(leaderboard ?? []);

    return {
      kpis,
      chatStatusShare,
      volumeWeek: chatVolumeWeek,
      volumeMax: chatVolumeMax,
      assigneeBars,
      maxAssignee,
      agentChatRows: agentRows,
      meta,
      haQa,
      volumeEmpty: effective && !loading && chatVolumeWeek.every((d) => (d.open || 0) + (d.resolved || 0) === 0),
    };
  }, [
    chatOverview,
    transferStats,
    chatVolume,
    openByAssignee,
    agentChatOps,
    slaSettings,
    routingAgents,
    leaderboard,
    effective,
    loading,
  ]);

  return { chats, loading, error, refresh };
}
